"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import { ArrowRight, Eye, EyeOff, Fingerprint, LockKeyhole, ShieldCheck } from "lucide-react";
import MfaEnrollment from "./MfaEnrollment";
import styles from "./login.module.css";

type Props = { brand: "Centro Controllo Jarvis" | "Jarvis" };

export default function LoginForm({ brand }: Props) {
  const isCentro = brand === "Centro Controllo Jarvis";
  const [mode, setMode] = useState<"login" | "totp" | "setup">("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const totpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("setup") === "1") setMode("setup");
  }, []);

  useEffect(() => {
    if (mode !== "totp") return;
    const frame = window.requestAnimationFrame(() => totpInputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [mode]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        cache: "no-store",
        body: JSON.stringify({ username: username.trim(), password, ...(mode === "totp" ? { code } : {}) }),
      });
      const result = (await response.json().catch(() => ({}))) as { error?: string; requiresTotp?: boolean; mfaSetupRequired?: boolean };
      if (!response.ok) {
        setError(result.error || "Non è stato possibile accedere. Riprova.");
        if (mode === "totp") window.requestAnimationFrame(() => totpInputRef.current?.focus());
        return;
      }
      if (result.requiresTotp) {
        setMode("totp");
        setError("");
        return;
      }
      if (result.mfaSetupRequired) {
        window.location.replace("/login?setup=1");
        return;
      }
      window.location.replace("/");
    } catch {
      setError("Connessione non disponibile. Riprova tra poco.");
    } finally {
      setBusy(false);
    }
  }

  if (mode === "setup") {
    return (
      <main className={styles.screen}>
        <div className={styles.grid} aria-hidden="true" />
        <div className={styles.ambient} aria-hidden="true" />
        <section className={`${styles.card} ${styles.setupCard}`} aria-labelledby="login-title">
          <div className={styles.brandMark} aria-hidden="true">
            <div className={styles.orbit} />
            <div className={styles.core}><Fingerprint size={27} strokeWidth={1.5} /></div>
            <span className={styles.signal} />
          </div>
          <MfaEnrollment brand={brand} loginPath="/login" />
        </section>
        <footer className={styles.footer}>DELIBERTI BEAUTY LAB <i /> SPAZIO PERSONALE</footer>
      </main>
    );
  }

  return (
    <main className={styles.screen}>
      <div className={styles.grid} aria-hidden="true" />
      <div className={styles.ambient} aria-hidden="true" />

      <section className={styles.card} aria-labelledby="login-title">
        <div className={styles.brandMark} aria-hidden="true">
          <div className={styles.orbit} />
          <div className={styles.core}><Fingerprint size={27} strokeWidth={1.5} /></div>
          <span className={styles.signal} />
        </div>

        <div className={styles.eyebrow}><span /> ACCESSO PRIVATO</div>
        <h1 id="login-title">{brand}<span>.</span></h1>
        <p className={styles.intro}>{mode === "totp" ? "Inserisci il codice temporaneo dell’app Authenticator o un codice di recupero." : isCentro ? "Accedi al Centro Controllo." : "Bentornato. Accedi al tuo spazio personale."}</p>

        <form className={styles.form} onSubmit={handleSubmit}>
          <label htmlFor="username">Nome utente</label>
          <input
            id="username"
            name="username"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="Il tuo nome utente"
            maxLength={64}
            required
          />

          <div className={styles.passwordLabel}>
            <label htmlFor="password">Password</label>
          </div>
          <div className={styles.passwordField}>
            <LockKeyhole size={16} aria-hidden="true" />
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Inserisci la password"
              maxLength={256}
              required
            />
            <button
              className={styles.reveal}
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              aria-label={showPassword ? "Nascondi password" : "Mostra password"}
            >
              {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>

          {mode === "totp" && (
            <>
              <div className={styles.passwordLabel}><label htmlFor="login-code">Codice Authenticator</label></div>
              <input
                ref={totpInputRef}
                autoFocus
                className={styles.otpInput}
                id="login-code"
                name="login-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={32}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/[^0-9A-Za-z-]/g, "").slice(0, 32))}
                placeholder="Codice a 6 cifre o di recupero"
                required
              />
            </>
          )}

          {error && <p className={styles.error} role="alert">{error}</p>}

          <button className={styles.submit} type="submit" disabled={busy || !username || !password || (mode === "totp" && !code)}>
            <span>{busy ? "Verifica in corso…" : mode === "totp" ? "Verifica e accedi" : isCentro ? "Accedi al Centro" : "Accedi a Jarvis"}</span>
            <ArrowRight size={17} aria-hidden="true" />
          </button>
        </form>

        <div className={styles.secure}>
          <ShieldCheck size={15} aria-hidden="true" />
          <span>{isCentro ? "Accesso con autenticazione a due fattori" : "Connessione protetta e privata"}</span>
        </div>
      </section>

      <footer className={styles.footer}>DELIBERTI BEAUTY LAB <i /> {isCentro ? "CENTRO CONTROLLO" : "SPAZIO PERSONALE"}</footer>
    </main>
  );
}
